import {
	ApiResponse,
	Body,
	EventResultType,
	Params,
	Query,
	RequestConfig,
	RequestEvent,
	RequestHost,
} from "./ApiTypes";

import * as ApiUtils from "./ApiUtils";
import RequestContext from "./RequestContext";
import RequestBackend from "./backend/RequestBackend";

export const MAX_RETRIES = 5;

const locks: Record<string, RequestContext> = {};
const runningOperations: Record<string, Promise<ApiResponse>> = {};

const backendLoaders = {
	axios: () => require("./backend/AxiosBackend"),
	fetch: () => import("./backend/FetchBackend"),
};
const preferredBackend: keyof typeof backendLoaders = "axios";
const backends: Record<string, RequestBackend> = {};

const getBackend = async () => {
	let backend = backends[preferredBackend];
	if (!backend) {
		backend = backends[preferredBackend] = new (
			(await backendLoaders[preferredBackend]()) as any
		).default();
	}
	return backend;
};

export const submit = async <
	R,
	P extends Params | undefined,
	Q extends Query | undefined,
	B extends Body | undefined,
>(
	host: RequestHost,
	config: RequestConfig<R, P, Q, B>,
): Promise<ApiResponse<R>> => {
	const computedConfig: RequestConfig<R, P, Q, B> = host.computeConfig(config);

	const context = new RequestContext<R, P, Q, B>(
		host,
		computedConfig,
		host.computePath(host.path, config as RequestConfig),
	);

	// if we are already running this request just return the same promise, no need to do it again
	const { key } = context;
	const sameRequest = runningOperations[key];
	if (context.method === "get" && sameRequest) {
		return sameRequest;
	}

	const { lock } = context.computedConfig.options || {};

	const computedLock = typeof lock === "function" ? lock(context) : lock;

	if (computedLock) {
		const lockedContext = locks[computedLock];
		if (lockedContext && lockedContext.id !== context.id) {
			lockedContext.cancel();
		}
		locks[computedLock] = context as any;
	}

	try {
		let response = await (runningOperations[key] = makeRequest(
			context as RequestContext<R>,
		));

		const successEventResult = await context.triggerEvent(RequestEvent.Success);
		if (
			successEventResult &&
			successEventResult.type === EventResultType.Respond
		) {
			context.response = response = successEventResult.response;
		}

		delete runningOperations[key];
		return response;
	} catch (error) {
		delete runningOperations[key];
		throw error;
	} finally {
		if (computedLock) {
			delete locks[computedLock];
		}
	}
};

const makeRequest = async <R>(
	context: RequestContext<R>,
): Promise<ApiResponse<R>> => {
	const backend = await getBackend();

	context.stats.attempt++;

	const beforeSendEventResult = await context.triggerEvent(
		RequestEvent.BeforeSend,
	);
	if (
		beforeSendEventResult &&
		beforeSendEventResult.type === EventResultType.Respond
	) {
		return (context.response = beforeSendEventResult.response);
	}

	try {
		const { promise, canceler } = backend.makeRequest<R>(context);
		context.addCanceller(canceler);
		const response = await promise;
		context.response = response;
		return response;
	} catch (error) {
		if (context.cancelled) {
			error.isCancelledRequest = true;
		}

		context.error = error;
		const errorResponse = await backend.extractResponseFromError(error);
		if (errorResponse !== undefined) {
			context.response = errorResponse;
			error.response = errorResponse;
		}

		// transform array buffer responses to objs
		if (context.response) {
			ApiUtils.parseResponseDataToObject(context.response);
		}

		const errorEventResult = await context.triggerEvent(RequestEvent.Error);
		if (errorEventResult?.type === EventResultType.Respond) {
			return errorEventResult.response;
		} else if (errorEventResult?.type === EventResultType.Cancel) {
			throw error;
		}

		const shouldRetry =
			errorEventResult?.type === EventResultType.Retry ||
			ApiUtils.isNetworkError(error);

		// retry request with same config
		if (
			shouldRetry &&
			context.stats.attempt <
				(context.computedConfig.options?.maxRetries ?? MAX_RETRIES)
		) {
			return makeRequest(context);
		}

		const unrecoverableErrorEventResult = await context.triggerEvent(
			RequestEvent.UnrecoverableError,
		);
		if (unrecoverableErrorEventResult) {
			if (unrecoverableErrorEventResult.type === EventResultType.Respond) {
				return unrecoverableErrorEventResult.response;
			}
		}
		throw error;
	}
};
